import { useCallback } from 'react'
import { RowsPerPageType, usePagination } from '@node-space/ui-components'
import { MIN_PAGINATION_SIZE } from 'constants/queries'

interface UsePaginationWithApiProps {
  onPageChangeApi?: (params: { paging: { offset: number } }) => void
  onRowsPerPageChangeApi?: (params: {
    paging: { offset: number; max: number }
    pagingMax: number
  }) => void
  executeQuery?: () => void
  initialRowsPerPage?: RowsPerPageType
}

/**
 * Use this hook for server-side pagination with API calls.
 * For client-side pagination where all data is loaded at once, use useSimplePagination instead.
 */
export const usePaginationWithApi = ({
  onPageChangeApi,
  onRowsPerPageChangeApi,
  executeQuery,
  initialRowsPerPage = MIN_PAGINATION_SIZE,
}: UsePaginationWithApiProps = {}) => {
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = usePagination({
    rowsPerPage: initialRowsPerPage,
  })

  const handleChangePage = useCallback(
    (newPage: number) => {
      onChangePage(newPage)
      const offset = newPage * rowsPerPage
      onPageChangeApi?.({
        paging: {
          offset,
        },
      })
    },
    [onChangePage, rowsPerPage, onPageChangeApi]
  )

  const handleChangeRowsPerPage = useCallback(
    (newRowsPerPage: RowsPerPageType) => {
      onChangeRowsPerPage(newRowsPerPage)
      onChangePage(0)

      onRowsPerPageChangeApi?.({
        paging: { offset: 0, max: newRowsPerPage },
        pagingMax: newRowsPerPage,
      })

      if (executeQuery) {
        setTimeout(() => {
          executeQuery()
        }, 0)
      }
    },
    [onChangeRowsPerPage, onChangePage, onRowsPerPageChangeApi, executeQuery]
  )

  return {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  }
}
